import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Box,
    Typography,
    IconButton,
} from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { monthOptions } from './MonthOption/monthOptions';
import { filterByDateRange } from './MonthOption/filterByDateRange';
import PrintIcon from '@mui/icons-material/Print';
import { styled, keyframes } from '@mui/system';
import ScrollToTopButton from '../Button/ScrollToTopButton';
import HomeIcon from '@mui/icons-material/Home';
import { motion } from 'framer-motion';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const slideInFromLeft = keyframes`
  0% {
    transform: translateX(-20px);
    opacity: 0;
  }
  100% {
    transform: translateX(0);
    opacity: 1;
  }
`;

const AnimatedTypography = styled(Typography)(({ theme }) => ({
    animation: `${slideInFromLeft} 0.5s ease-out`,
}));

const ProfitAndLoss = () => {
    const [user, setUser] = useState({});
    const [token, setToken] = useState(() => localStorage.getItem('token') || null);
    const [loading, setLoading] = useState(false);
    const [orders, setOrders] = useState([]);
    const [damages, setDamages] = useState([]);
    const [expenses, setExpenses] = useState([]);
    const [stocks, setStocks] = useState([]);
    const [products, setProducts] = useState([]);
    const [searchDate, setSearchDate] = useState('');
    const [selectedMonth, setSelectedMonth] = useState('');
    const [dateRange, setDateRange] = useState('');
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [openingStockValue, setOpeningStockValue] = useState(0);
    const [closingStockValue, setClosingStockValue] = useState(0);
    const [remainStockValue, setRemainStockValue] = useState(0);
    const navigate = useNavigate();

    const toggleSearch = () => {
        setIsSearchOpen(!isSearchOpen);
    };

    useEffect(() => {
        if (token) {
            localStorage.setItem('token', token);
        }
    }, [token]);

    useEffect(() => {
        const fetchProfile = async () => {
            const token = localStorage.getItem("token");

            if (!token) {
                navigate("/");
                return;
            }

            try {
                const response = await axios.get("https://admin.bizstor.lk/public/api/profile", {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setUser(response.data);
            } catch (error) {
                if (error.response && error.response.status === 401) {
                    localStorage.removeItem("token");
                    navigate("/");
                }
            }
        };

        fetchProfile();
    }, [navigate]);

    useEffect(() => {
        if (user.id) {
            fetchData();
        }
    }, [user.id]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [ordersResponse, damagesResponse, expensesResponse, stocksResponse, productResponse] = await Promise.all([
                axios.get('https://admin.bizstor.lk/public/api/get-data'),
                axios.get('https://admin.bizstor.lk/public/api/get-damage-data'),
                axios.get('https://admin.bizstor.lk/public/api/get-expense-data'),
                axios.get('https://admin.bizstor.lk/public/api/get-stock-data'),
                axios.get('https://admin.bizstor.lk/public/api/get-product-data')
            ]);

            if (user.id) {
                const userId = user.id.toString().toLowerCase();
                setOrders(ordersResponse.data.filter(order => order.usertoken.toLowerCase() === userId));
                setDamages(damagesResponse.data.filter(damage => damage.usertoken.toLowerCase() === userId));
                setExpenses(expensesResponse.data.filter(expense => expense.usertoken.toLowerCase() === userId));
                setStocks(stocksResponse.data.filter(stock => stock.usertoken.toLowerCase() === userId));
                setProducts(productResponse.data.products.filter(product => product.usertoken.toLowerCase() === userId));
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const getBpriceForProduct = (productName) => {
        const matchingProduct = products.find((product) => product.product === productName);
        return matchingProduct ? parseFloat(matchingProduct.Bprice) || 0 : 0;
    };

    useEffect(() => {
        const calculateStockValues = () => {
            const currentYear = new Date().getFullYear();
            let openingStockTotal = 0;
            let closingStockTotal = 0;

            // Calculate Remaining Stock Value (Default)
            const totalStockValue = stocks.reduce((acc, stock) => {
                stock.items.forEach(item => {
                    acc += getBpriceForProduct(item.product) * item.quantity;
                });
                return acc;
            }, 0);

            const totalSoldValue = orders.reduce((acc, order) => {
                order.items.forEach(item => {
                    acc += getBpriceForProduct(item.product) * item.quantity;
                });
                return acc;
            }, 0);

            setRemainStockValue(totalStockValue - totalSoldValue);

            if (selectedMonth) {
                const month = parseInt(selectedMonth, 10);
                // Opening Stock Calculation
                const openingStockEndDate = new Date(currentYear, month - 1, 0); // Last day of previous month
                const stocksForOpening = stocks.filter(stock => new Date(stock.date) <= openingStockEndDate);
                const ordersForOpening = orders.filter(order => new Date(order.invoicedate) <= openingStockEndDate);

                const totalOpeningStock = stocksForOpening.reduce((acc, stock) => {
                    stock.items.forEach(item => {
                        acc += getBpriceForProduct(item.product) * item.quantity;
                    });
                    return acc;
                }, 0);

                const totalOpeningSold = ordersForOpening.reduce((acc, order) => {
                    order.items.forEach(item => {
                        acc += getBpriceForProduct(item.product) * item.quantity;
                    });
                    return acc;
                }, 0);
                openingStockTotal = totalOpeningStock - totalOpeningSold;

                // Closing Stock Calculation
                const closingStockEndDate = new Date(currentYear, month, 0); // Last day of selected month
                const stocksForClosing = stocks.filter(stock => new Date(stock.date) <= closingStockEndDate);
                const ordersForClosing = orders.filter(order => new Date(order.invoicedate) <= closingStockEndDate);

                const totalClosingStock = stocksForClosing.reduce((acc, stock) => {
                    stock.items.forEach(item => {
                        acc += getBpriceForProduct(item.product) * item.quantity;
                    });
                    return acc;
                }, 0);

                const totalClosingSold = ordersForClosing.reduce((acc, order) => {
                    order.items.forEach(item => {
                        acc += getBpriceForProduct(item.product) * item.quantity;
                    });
                    return acc;
                }, 0);

                closingStockTotal = totalClosingStock - totalClosingSold;
            }

            setOpeningStockValue(openingStockTotal);
            setClosingStockValue(closingStockTotal);
        };

        if (stocks.length > 0 && products.length > 0) {
            calculateStockValues();
        }

    }, [selectedMonth, stocks, orders, products]);


    const handleSearchChange = (event) => {
        setSearchDate(event.target.value);
    };

    const handleMonthChange = (event) => {
        setSelectedMonth(event.target.value);
    };

    const handleDateRangeChange = (event) => {
        setDateRange(event.target.value);
    };

    const filterByDate = (data, dateKey) => {
        if (!searchDate) return data;
        return data.filter(item => new Date(item[dateKey]).toDateString() === new Date(searchDate).toDateString());
    };

    const filterByMonth = (data, dateKey) => {
        if (!selectedMonth) return data;
        return data.filter(item => {
            const date = new Date(item[dateKey]);
            return date.getMonth() + 1 === parseInt(selectedMonth);
        });
    };

    const filteredOrders = filterByDate(filterByMonth(filterByDateRange(orders, 'invoicedate', dateRange), 'invoicedate'), 'invoicedate');
    const filteredExpenses = filterByDate(filterByMonth(filterByDateRange(expenses, 'date', dateRange), 'date'), 'date');

    const groupedOrders = filteredOrders.reduce((acc, order) => {
        order.items.forEach(item => {
            const key = `${item.product}`;
            if (!acc[key]) {
                acc[key] = {
                    product: item.product,
                    rate: item.rate,
                    quantity: 0,
                    totalPrice: 0,
                    totalDiscount: 0,
                    totalTax: 0,
                    total: 0,
                };
            }
            acc[key].quantity += item.quantity;
            acc[key].totalPrice += item.rate * item.quantity;
            acc[key].totalDiscount += (item.rate * item.quantity * order.discount) / 100;
            acc[key].totalTax += (item.rate * item.quantity * item.tax) / 100;
            acc[key].total = acc[key].totalPrice - acc[key].totalDiscount + acc[key].totalTax;
        });
        return acc;
    }, {});

    const { grandTotal } = Object.values(groupedOrders).reduce(
        (totals, { total }) => {
            totals.grandTotal += total;
            return totals;
        },
        { grandTotal: 0 }
    );

    const costOfSales = Object.values(
        filteredExpenses.reduce((acc, expense) => {
            expense.items.forEach(item => {
                if (item.category.toLowerCase().trim().startsWith('cost of sales')) {
                    if (!acc[item.category]) {
                        acc[item.category] = { category: item.category, totalAmount: 0 };
                    }
                    acc[item.category].totalAmount += Number(item.amount);
                }
            });
            return acc;
        }, {})
    );

    const costOfSalesFromExpenses = costOfSales.reduce((total, { totalAmount }) => total + totalAmount, 0);
    const costOfSalesTotalAmount = (openingStockValue + costOfSalesFromExpenses) - closingStockValue;

    const printReport = () => {
        // Implementation for printing the report
    };

    const handleBackClick = () => {
        navigate("/allreport");
    };

    const expensesTotalAmount = filteredExpenses.reduce((total, expense) => {
        return total + expense.items
            .filter(item => !item.category.toLowerCase().trim().startsWith('cost of sales'))
            .reduce((itemTotal, item) => itemTotal + Number(item.amount), 0);
    }, 0).toFixed(2);


    return (
        <Box sx={{ mt: 10 }}>
            <AnimatedTypography
                component="h1"
                sx={{
                    ml: { xs: 2, sm: 3 },
                    mt: { xs: 2, sm: 3 },
                    letterSpacing: '0.01em',
                    fontSize: { xs: '20px', sm: '24px' },
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                    <HomeIcon sx={{ ml: 1, fontSize: '18px', color: '#6d7081' }} />
                    <Typography component="span" sx={{ ml: 0.5, fontSize: '14px', color: '#6d7081' }}>
                        - Report - Profit and Loss
                    </Typography>
                </Box>
            </AnimatedTypography>

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '95%',
                    margin: 'auto',
                    padding: '10px',
                    bgcolor: 'background.paper',
                    border: '1px solid #EDF2F7',
                    borderRadius: '16px',
                    marginTop: '30px',
                    position: 'relative',
                    '@media (max-width: 600px)': { marginTop: '10px' },
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        p: 2,
                        gap: 2,
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'left', '@media (max-width: 600px)': { margin: 'auto' } }}>
                        <Box sx={{ color: '#1e293b' }}><h2>Profit & Loss</h2></Box>
                        <Box component="div" sx={{ mb: { sm: 2, xs: 'none' }, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', color: 'primary.main', width: 'fit-content', '&:hover': { color: 'primary.main' } }} onClick={handleBackClick}>
                            <ArrowBackIcon fontSize="small" />
                            <Typography variant="body2" sx={{ ml: 0.5, fontWeight: 500 }}>
                                Back to Report
                            </Typography>
                        </Box>
                    </Box>

                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: isSearchOpen ? 1 : 0, height: isSearchOpen ? 'auto' : 0 }} transition={{ duration: 0.3 }} style={{ overflow: 'hidden' }}>
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, width: '80%', alignItems: 'center', '@media (max-width: 600px)': { margin: 'auto' } }}>
                            <Select variant="outlined" value={selectedMonth} onChange={handleMonthChange} displayEmpty size="small" margin="normal" sx={{ width: '250px', height: '40px', mt: 1, borderRadius: '8px', '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
                                {monthOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                ))}
                            </Select>
                            <Select variant="outlined" value={dateRange} onChange={handleDateRangeChange} displayEmpty size="small" margin="normal" sx={{ width: '250px', height: '40px', mt: 1, borderRadius: '8px', '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
                                <MenuItem value=""><em>All</em></MenuItem>
                                <MenuItem value="last7Days">Last 7 Days</MenuItem>
                                <MenuItem value="lastMonth">Last Month</MenuItem>
                                <MenuItem value="last3Months">Last 3 Months</MenuItem>
                                <MenuItem value="lastYear">Last Year</MenuItem>
                            </Select>
                        </Box>
                    </motion.div>

                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton onClick={toggleSearch} sx={{ display: { xs: 'flex', sm: 'none' } }}>
                            <SearchIcon />
                        </IconButton>
                        <IconButton onClick={printReport}><PrintIcon /></IconButton>
                    </Box>
                </Box>

                <Box sx={{ display: { sm: 'flex', xs: 'none' }, flexDirection: 'row', width: '100%', height: '50px', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '20%' }}>
                        <Select variant="outlined" value={selectedMonth} onChange={handleMonthChange} displayEmpty size="small" margin="normal" sx={{ bgcolor: 'rgba(240, 240, 240, 0.17)', width: '245px', height: '40px', borderRadius: '8px', '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
                            {monthOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                            ))}
                        </Select>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '20%' }}>
                        <Select variant="outlined" value={dateRange} onChange={handleDateRangeChange} displayEmpty size="small" margin="normal" sx={{ bgcolor: 'rgba(240, 240, 240, 0.17)', width: '245px', height: '40px', borderRadius: '8px', '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}>
                            <MenuItem value=""><em>All</em></MenuItem>
                            <MenuItem value="last7Days">Last 7 Days</MenuItem>
                            <MenuItem value="lastMonth">Last Month</MenuItem>
                            <MenuItem value="last3Months">Last 3 Months</MenuItem>
                            <MenuItem value="lastYear">Last Year</MenuItem>
                        </Select>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '60%' }}></Box>
                </Box>

                <Typography variant="h6" component="h1" gutterBottom sx={{ ml: 3 }}>Income</Typography>
                <TableContainer sx={{ marginTop: 2, margin: 'auto', maxWidth: '95%' }}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ '& td, & th': { padding: '9px 13px' } }}>
                                <TableCell sx={{ textAlign: 'left' }}><Typography variant="subtitle2" fontWeight="bold">Sales</Typography></TableCell>
                                <TableCell sx={{ textAlign: 'right' }}><Typography variant="subtitle2" fontWeight="bold">TOTAL</Typography></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {Object.values(groupedOrders).map(({ product, total }, index) => (
                                <TableRow key={`grouped-order-${index}`} sx={{ '& td, & th': { padding: '9px 13px' } }}>
                                    <TableCell sx={{ textAlign: 'left', color: '#636363', fontSize: '14px' }}>{product}</TableCell>
                                    <TableCell sx={{ textAlign: 'right', color: '#636363', fontSize: '14px' }}>{Number(total).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}</TableCell>
                                </TableRow>
                            ))}
                            <TableRow>
                                <TableCell sx={{ textAlign: 'left', fontWeight: 'bold', fontSize: '14px' }}>Total Income</TableCell>
                                <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', fontSize: '14px' }}>LKR.{Number(grandTotal).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>


                <Typography variant="h6" component="h1" gutterBottom sx={{ mb: 2, mt: 3, ml: 3 }}>Cost Of Sales</Typography>
                <TableContainer sx={{ marginTop: 2, margin: 'auto', maxWidth: '95%' }}>
                    <Table>
                        <TableBody>
                            {selectedMonth !== '' && (
                                <TableRow sx={{ '& td, & th': { padding: '9px 13px' } }}>
                                    <TableCell sx={{ textAlign: 'left', color: '#636363', fontSize: '14px' }}>Opening Stock</TableCell>
                                    <TableCell sx={{ textAlign: 'right', color: '#636363', fontSize: '14px' }}>{Number(openingStockValue).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}</TableCell>
                                </TableRow>
                            )}
                            {costOfSales.map(({ category, totalAmount }, index) => (
                                <TableRow key={`cos-${index}`} sx={{ '& td, & th': { padding: '9px 13px' } }}>
                                    <TableCell sx={{ textAlign: 'left', color: '#636363', fontSize: '14px' }}>{category.replace(/^cost of sales\s*-\s*/i, '')}</TableCell>
                                    <TableCell sx={{ textAlign: 'right', color: '#636363', fontSize: '14px' }}> {Number(totalAmount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}</TableCell>
                                </TableRow>
                            ))}
                            {selectedMonth !== '' && (
                                <TableRow sx={{ '& td, & th': { padding: '9px 13px' } }}>
                                    <TableCell sx={{ textAlign: 'left', color: '#636363', fontSize: '14px' }}>Closing Stock</TableCell>
                                    <TableCell sx={{ textAlign: 'right', color: '#636363', fontSize: '14px' }}>{Number(closingStockValue).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}</TableCell>
                                </TableRow>
                            )}
                            <TableRow>
                                <TableCell sx={{ textAlign: 'left', fontWeight: 'bold', fontSize: '14px' }}>Total Cost of Sales</TableCell>
                                <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', fontSize: '14px' }}>LKR.{Number(costOfSalesTotalAmount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ textAlign: 'left', fontWeight: 'bold', fontSize: '14px' }}>Total Remain Stock Value</TableCell>
                                <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', fontSize: '14px' }}>LKR.{Number(remainStockValue).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell sx={{ textAlign: 'left', fontWeight: 'bold', fontSize: '14px' }}>Gross Profit</TableCell>
                                <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', fontSize: '14px' }}>LKR.{(Number(grandTotal) + Number(remainStockValue) - Number(costOfSalesTotalAmount)).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
                {/**<Typography variant="h6" component="h1" gutterBottom sx={{ mt: 3, ml: 3 }}>Remaining Stock Value</Typography>
                <TableContainer sx={{ marginTop: 2, margin: 'auto', maxWidth: '95%' }}>
                    <Table>
                        <TableBody>
                            <TableRow>
                                <TableCell sx={{ textAlign: 'left', fontWeight: 'bold', fontSize: '14px' }}>Total Remain Stock</TableCell>
                                <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', fontSize: '14px' }}>LKR.{Number(remainStockValue).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>*/}

                <Typography variant="h6" component="h1" gutterBottom sx={{ mb: 2, mt: 3, ml: 3 }}>Expenses</Typography>
                <TableContainer sx={{ marginTop: 2, margin: 'auto', maxWidth: '95%' }}>
                    <Table>
                        <TableBody>
                            {filteredExpenses.map((expense) => (
                                expense.items
                                    .filter(item => !item.category.toLowerCase().trim().startsWith('cost of sales'))
                                    .map((item, index) => (
                                        <TableRow key={`expense-${index}`} sx={{ '& td, & th': { padding: '9px 13px' } }}>
                                            <TableCell sx={{ textAlign: 'left', color: '#636363', fontSize: '14px' }}>{item.category}</TableCell>
                                            <TableCell sx={{ textAlign: 'right', color: '#636363', fontSize: '14px' }}>{Number(item.amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}</TableCell>
                                        </TableRow>
                                    ))
                            ))}
                            <TableRow>
                                <TableCell sx={{ textAlign: 'left', fontWeight: 'bold', fontSize: '14px' }}>Total Expenses</TableCell>
                                <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', fontSize: '14px' }}>LKR.{expensesTotalAmount.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>

                <TableContainer sx={{ marginTop: 2, margin: 'auto', maxWidth: '95%' }}>
                    <Table>
                        <TableBody>
                            {/**<TableRow>
                                <TableCell sx={{ textAlign: 'left' }} colSpan={1}><Typography sx={{ fontSize: '14px', fontWeight: 'bold' }}><b>Net Earnings</b></Typography></TableCell>
                                <TableCell sx={{ textAlign: 'right' }} colSpan={1}>
                                    <Typography sx={{ fontSize: '14px', fontWeight: 'bold' }}>
                                        <b>LKR.{(Number(grandTotal) + Number(remainStockValue) - Number(costOfSalesTotalAmount) - Number(expensesTotalAmount || 0)).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}</b>
                                    </Typography>
                                </TableCell>
                            </TableRow>*/}
                            <TableRow>
                                <TableCell sx={{ textAlign: 'left', fontWeight: 'bold', fontSize: '14px' }}>Net Earnings</TableCell>
                                <TableCell sx={{ textAlign: 'right', fontWeight: 'bold', fontSize: '14px' }}>LKR.{(Number(grandTotal) + Number(remainStockValue) - Number(costOfSalesTotalAmount) - Number(expensesTotalAmount || 0)).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}</TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {loading && (
                <Box sx={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'white', zIndex: 9999 }}>
                    <l-trio size="50" speed="1.3" color="red"></l-trio>
                </Box>
            )}
            <ScrollToTopButton />
        </Box>
    );
};

export default ProfitAndLoss;